/**
 * Utility functions for content management components
 */
import { supabase } from '@/lib/supabase'
import { Content, ScheduledPostStatus } from '../useContentData';
import { SortConfig, StatusFilter } from '../types/contentManagement';
// Constants
export const STATIC_CONTENT_API_URL = `${import.meta.env.VITE_WORKERS_URL}/rainfactory/content-functions/serve-static`

/**
 * Check if a platform is connected for a brand
 */
export async function checkPlatformConnection(platformKey: string, brandId: string): Promise<boolean> {
  try {
    // Check connection status in platform_brand_settings
    const { data, error } = await supabase
      .from('platform_brand_settings')
      .select('*, platforms(platform_key)')
      .eq('brand_id', brandId)
      .eq('platforms.platform_key', platformKey)
      .eq('status', 'CONNECTED')
      .single();

    if (error) {
      console.error('Error checking platform connection:', error);
      return false;
    }

    // If we found a record with CONNECTED status, the platform is connected
    return !!data;
  } catch (error) {
    console.error('Error checking platform connection:', error);
    return false;
  }
}

/**
 * Get URL for static content
 */
export function getStaticContentUrl(contentId: string, type: string, versionNumber?: number): string {
  const baseUrl = `${STATIC_CONTENT_API_URL}?content_id=${contentId}&resource_type=${type}`;
  const versionParam = versionNumber ? `&version=${versionNumber}` : '';
  return `${baseUrl}${versionParam}`;
}

/**
 * Helper to revert content to a previous version
 */
export async function revertContentToVersion(contentId: string, versionNumber: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .rpc('revert_content_version', {
        p_content_id: contentId,
        p_version_number: versionNumber
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error reverting version:', error);
    return false;
  }
}

/**
 * Formats a platform name for display
 */
export function formatPlatformName(platform: string): string {
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

/**
 * Status definitions and helper functions
 */
export type AvailableStatus =
  | 'READY'
  | 'PUBLISHED'
  | 'ARCHIVED'
  | 'SCHEDULED'
  | 'DRAFT'
  | 'GENERATING'
  | 'PROCESSING'
  | 'FAILED'
  | 'HOLD'
  | 'HELD'
  | 'CANCELLED'
  | 'DELETED'
  | 'ERROR';

// User-settable statuses (matching database enum)
export const editableStatuses: AvailableStatus[] = [
  'GENERATING',
  'READY',
  'SCHEDULED',
  'DRAFT',
  'PUBLISHED',
  'ERROR',
  'ARCHIVED',
  'DELETED'
];

// All statuses including computed ones (for filtering/display)
export const availableStatuses: AvailableStatus[] = [
  'GENERATING',
  'READY',
  'SCHEDULED',
  'DRAFT',
  'PUBLISHED',
  'PROCESSING',
  'FAILED',
  'HOLD',
  'HELD',
  'CANCELLED',
  'ERROR',
  'ARCHIVED',
  'DELETED'
];

export function getStatusLabel(status: AvailableStatus): string {
  const labels: Record<AvailableStatus, string> = {
    'READY': 'Unpublished',
    'PUBLISHED': 'Published',
    'ARCHIVED': 'Archived',
    'SCHEDULED': 'Scheduled',
    'DRAFT': 'Draft',
    'GENERATING': 'Generating',
    'PROCESSING': 'Processing',
    'FAILED': 'Failed',
    'HOLD': 'On Hold',
    'HELD': 'Held',
    'CANCELLED': 'Cancelled',
    'DELETED': 'Deleted',
    'ERROR': 'Error'
  };
  return labels[status];
}

/**
 * Extracts image URL from content JSON structure
 */
export const getContentImage = (content: Content): string | null => {
  try {
    if (content.content_json?.image_url) {
      return content.content_json.image_url;
    }
    // Check for social post structure (LinkedIn, Twitter, etc.)
    if (content.content_json?.posts && Array.isArray(content.content_json.posts) && content.content_json.posts.length > 0) {
      const firstPost = content.content_json.posts[0];
      if (firstPost.image_url) {
        return firstPost.image_url;
      }
      // Also check for images array in the post
      if (firstPost.images && Array.isArray(firstPost.images) && firstPost.images.length > 0) {
        return firstPost.images[0];
      }
    }
    if (content.content_json?.images && Array.isArray(content.content_json.images) && content.content_json.images.length > 0) {
      return content.content_json.images[0];
    }
    if (content.content_json?.media && Array.isArray(content.content_json.media)) {
      const imageMedia = content.content_json.media.find((m: any) => m.type === 'image');
      if (imageMedia?.url) return imageMedia.url;
    }
  } catch (error) {
    console.error('Error extracting image from content:', error);
  }
  return null;
};

/**
 * Checks if content contains video
 */
export const hasVideo = (content: Content): boolean => {
  try {
    if (content.content_json?.video_url) return true;
    // Check for social post structure
    if (content.content_json?.posts && Array.isArray(content.content_json.posts) && content.content_json.posts.length > 0) {
      const firstPost = content.content_json.posts[0];
      if (firstPost.video_url) return true;
      if (firstPost.videos && Array.isArray(firstPost.videos) && firstPost.videos.length > 0) {
        return true;
      }
    }
    if (content.content_json?.media && Array.isArray(content.content_json.media)) {
      return content.content_json.media.some((m: any) => m.type === 'video');
    }
  } catch (error) {
    console.error('Error checking for video in content:', error);
  }
  return false;
};

/**
 * Filters content based on search, type, status, and platform criteria
 */
export const filterContent = (
  contents: Content[],
  search: string,
  contentType: string,
  statusFilter: StatusFilter,
  platformFilter: string = 'all'
): Content[] => {
  return contents.filter(content => {
    const matchesSearch = content.name.toLowerCase().includes(search.toLowerCase())
    const matchesType = contentType === 'all' || content.content_type === contentType
    const matchesPlatform = platformFilter === 'all' || content.platform_key === platformFilter

    // Status filter logic - now using the computed display_status for consistency
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const displayStatus = content.display_status || content.status;

      switch (statusFilter) {
        case 'published':
          matchesStatus = displayStatus === 'PUBLISHED' || content.is_published === true
          break;
        case 'scheduled':
          matchesStatus = displayStatus === 'SCHEDULED' || content.is_scheduled === true
          break;
        case 'held':
          matchesStatus = displayStatus === 'HOLD' || displayStatus === 'HELD'
          break;
        case 'draft':
          matchesStatus = displayStatus === 'DRAFT'
          break;
        case 'ready':
          matchesStatus = displayStatus === 'READY' || content.is_ready === true
          break;
        case 'archived':
          matchesStatus = displayStatus === 'ARCHIVED' || content.status === 'ARCHIVED'
          break;
        case 'deleted':
          matchesStatus = displayStatus === 'DELETED' || content.status === 'DELETED'
          break;
        case 'generating':
          matchesStatus = displayStatus === 'GENERATING' || content.status === 'GENERATING'
          break;
        case 'processing':
          matchesStatus = displayStatus === 'PROCESSING' || content.is_processing === true
          break;
        case 'failed':
          matchesStatus = displayStatus === 'FAILED' || content.is_failed === true
          break;
        case 'error':
          matchesStatus = displayStatus === 'ERROR' || content.status === 'ERROR'
          break;
        case 'cancelled':
          matchesStatus = displayStatus === 'CANCELLED'
          break;
        case 'scheduled_and_ready':
          matchesStatus = displayStatus === 'SCHEDULED' || displayStatus === 'READY' ||
            content.is_scheduled === true || content.is_ready === true
          break;
        case 'scheduled_and_held':
          matchesStatus = displayStatus === 'SCHEDULED' || displayStatus === 'HOLD' || displayStatus === 'HELD' ||
            content.is_scheduled === true
          break;
        default:
          matchesStatus = true;
      }
    }

    return matchesSearch && matchesType && matchesStatus && matchesPlatform
  });
};

/**
 * Sorts content based on provided sort configuration
 */
export const sortContent = (contents: Content[], sortConfig: SortConfig): Content[] => {
  return [...contents].sort((a, b) => {
    if (sortConfig.key === 'published_at') {
      // Handle unpublished content by putting it at the end
      const dateA = a.published_at;
      const dateB = b.published_at;

      // If both are unpublished, maintain their relative order by created date
      if (!dateA && !dateB) {
        const createdA = new Date(a.created_at).getTime();
        const createdB = new Date(b.created_at).getTime();
        return sortConfig.direction === 'asc' ? createdA - createdB : createdB - createdA;
      }

      // If only one is unpublished, put it at the end
      if (!dateA) return sortConfig.direction === 'asc' ? 1 : -1;
      if (!dateB) return sortConfig.direction === 'asc' ? -1 : 1;

      // Both are published, sort by published date
      const timeA = new Date(dateA).getTime();
      const timeB = new Date(dateB).getTime();
      return sortConfig.direction === 'asc' ? timeA - timeB : timeB - timeA;
    }

    if (sortConfig.key === 'scheduled_time') {
      const dateA = a.scheduled_time;
      const dateB = b.scheduled_time;

      // If both are unscheduled, maintain their relative order by created date
      if (!dateA && !dateB) {
        const createdA = new Date(a.created_at).getTime();
        const createdB = new Date(b.created_at).getTime();
        return sortConfig.direction === 'asc' ? createdA - createdB : createdB - createdA;
      }

      // If only one is unscheduled, put it at the end
      if (!dateA) return sortConfig.direction === 'asc' ? 1 : -1;
      if (!dateB) return sortConfig.direction === 'asc' ? -1 : 1;

      // Both are scheduled, sort by scheduled time
      const timeA = new Date(dateA).getTime();
      const timeB = new Date(dateB).getTime();
      return sortConfig.direction === 'asc' ? timeA - timeB : timeB - timeA;
    }

    const valueA = a[sortConfig.key];
    const valueB = b[sortConfig.key];

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortConfig.direction === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    }

    if (valueA instanceof Date && valueB instanceof Date) {
      return sortConfig.direction === 'asc'
        ? valueA.getTime() - valueB.getTime()
        : valueB.getTime() - valueA.getTime();
    }

    return 0;
  });
};

/**
 * Transforms view data to Content object structure
 */
export const transformViewDataToContent = (viewData: any): Content | null => {
  if (!viewData) return null;

  const scheduledPostData: ScheduledPostStatus = viewData.scheduled_post_id ? {
    id: viewData.scheduled_post_id,
    status: viewData.scheduled_post_status || '',
    published_url: viewData.published_url,
    scheduled_for: viewData.scheduled_for,
    published_at: viewData.published_at,
    error_message: viewData.error_message,
    error_details: viewData.error_details,
    attempt_count: viewData.attempt_count,
    last_attempt_time: viewData.last_attempt_time
  } : null;

  return {
    id: viewData.id,
    campaign_id: viewData.campaign_id,
    brand_id: viewData.brand_id,
    content_type: viewData.content_type,
    current_version: viewData.current_version,
    content_json: viewData.content_json,
    name: viewData.name,
    status: viewData.content_status,
    created_at: viewData.created_at,
    updated_at: viewData.updated_at,
    platform_key: viewData.platform_key,
    scheduled_time: viewData.scheduled_for,
    published_at: viewData.published_at,
    publish_status: viewData.scheduled_post_status,
    scheduledPostData,
    display_status: viewData.display_status,
    effective_publish_time: viewData.effective_publish_time,
    is_published: viewData.is_published,
    is_scheduled: viewData.is_scheduled,
    is_ready: viewData.is_ready,
    is_draft: viewData.is_draft,
    campaigns: viewData.campaign_name ? {
      id: viewData.campaign_id,
      name: viewData.campaign_name,
      image_url: viewData.campaign_image_url
    } : null,
    convergence: null // Convergence data not included in this view transformation
  };
};